const VIDEO_SRC = "/nixi/nixi-jnr-home-test.mp4";
const MATTE_TRAVERSAL_LIMIT = 170;
const MATTE_TRANSPARENT_FLOOR = 8;
const MATTE_OPAQUE_FLOOR = 118;
const FRAME_SAFETY_SCALE = 1.15;

type FrameVideo = HTMLVideoElement & {
  requestVideoFrameCallback?: (callback: (now: number, metadata: unknown) => void) => number;
};

type FrameRegion = {
  left: number;
  right: number;
  top: number;
  bottom: number;
};

function removeConnectedBlackBackground(
  frame: ImageData,
  sourceFrame: FrameRegion,
) {
  const { data, width, height } = frame;
  const pixelCount = width * height;
  const visited = new Uint8Array(pixelCount);
  const queue = new Int32Array(pixelCount);

  const peakAt = (pixel: number) => {
    const offset = pixel * 4;
    return Math.max(data[offset], data[offset + 1], data[offset + 2]);
  };

  let start = 0;
  let end = 0;
  const add = (x: number, y: number) => {
    if (
      x < sourceFrame.left ||
      x > sourceFrame.right ||
      y < sourceFrame.top ||
      y > sourceFrame.bottom
    ) {
      return;
    }
    const pixel = y * width + x;
    if (visited[pixel] || peakAt(pixel) > MATTE_TRAVERSAL_LIMIT) return;
    visited[pixel] = 1;
    queue[end++] = pixel;
  };

  for (let x = sourceFrame.left; x <= sourceFrame.right; x += 1) {
    add(x, sourceFrame.top);
    add(x, sourceFrame.bottom);
  }
  for (let y = sourceFrame.top; y <= sourceFrame.bottom; y += 1) {
    add(sourceFrame.left, y);
    add(sourceFrame.right, y);
  }

  while (start < end) {
    const pixel = queue[start++];
    const x = pixel % width;
    const y = Math.floor(pixel / width);
    add(x - 1, y);
    add(x + 1, y);
    add(x, y - 1);
    add(x, y + 1);
  }

  // Convert the black-composited outer matte back into alpha. Dark glow pixels
  // become translucent coloured light; solid antenna and character pixels remain
  // opaque. Interior details are not connected to this outside traversal.
  for (let index = 0; index < end; index += 1) {
    const pixel = queue[index];
    const offset = pixel * 4;
    const peak = peakAt(pixel);
    const matteAlpha = Math.max(
      0,
      Math.min(
        1,
        (peak - MATTE_TRANSPARENT_FLOOR) /
          (MATTE_OPAQUE_FLOOR - MATTE_TRANSPARENT_FLOOR),
      ),
    );

    if (matteAlpha >= 1) continue;
    if (matteAlpha <= 0 || peak === 0) {
      data[offset + 3] = 0;
      continue;
    }

    const unmatteScale = 255 / peak;
    data[offset] = Math.min(255, Math.round(data[offset] * unmatteScale));
    data[offset + 1] = Math.min(255, Math.round(data[offset + 1] * unmatteScale));
    data[offset + 2] = Math.min(255, Math.round(data[offset + 2] * unmatteScale));
    data[offset + 3] = Math.min(data[offset + 3], Math.round(255 * matteAlpha));
  }

}

function clearPixVerseWatermark(frame: ImageData, sourceFrame: FrameRegion) {
  const { data, width } = frame;
  const sourceWidth = sourceFrame.right - sourceFrame.left + 1;
  const sourceHeight = sourceFrame.bottom - sourceFrame.top + 1;
  const left = sourceFrame.left + Math.floor(sourceWidth * 0.55);
  const right = sourceFrame.left + Math.ceil(sourceWidth * 0.98);
  const top = sourceFrame.top;
  const bottom = sourceFrame.top + Math.ceil(sourceHeight * 0.15);

  for (let y = top; y <= bottom; y += 1) {
    for (let x = left; x <= right; x += 1) {
      const offset = (y * width + x) * 4;
      const peak = Math.max(data[offset], data[offset + 1], data[offset + 2]);
      const low = Math.min(data[offset], data[offset + 1], data[offset + 2]);
      // The mark is neutral grey/white. Remove only those low-chroma pixels so
      // the coloured antenna glow beneath this area remains smooth and intact.
      if (peak >= 45 && peak - low <= 28) data[offset + 3] = 0;
    }
  }
}

function mountJnrHomeVideo() {
  const banner = document.querySelector<HTMLElement>(".jnr-nixi-home-banner");
  if (!banner || banner.querySelector('[data-jnr-home-video="true"]')) return;

  const companion = banner.querySelector<HTMLElement>('.jnr-nixi[data-context="home"]');
  if (!companion) return;

  companion.style.display = "none";

  const flightStage = document.createElement("button");
  flightStage.type = "button";
  flightStage.className = "jnr-home-video-flight";
  flightStage.setAttribute("aria-label", "NIXI animated companion. Tap to make NIXI bounce.");

  const canvas = document.createElement("canvas");
  canvas.setAttribute("data-jnr-home-video", "true");
  canvas.setAttribute("aria-hidden", "true");
  canvas.className = "jnr-home-video-canvas";
  canvas.hidden = true;

  let bounceTimer = 0;
  const bounce = () => {
    window.clearTimeout(bounceTimer);
    canvas.classList.remove("jnr-home-video-canvas--bounce");
    void canvas.offsetWidth;
    canvas.classList.add("jnr-home-video-canvas--bounce");
    bounceTimer = window.setTimeout(() => {
      canvas.classList.remove("jnr-home-video-canvas--bounce");
    }, 700);
  };
  flightStage.addEventListener("click", bounce);

  banner.style.minHeight = "clamp(23rem, 48vw, 30rem)";

  const fitFlightArea = () => {
    const bounds = banner.getBoundingClientRect();
    const phoneLayout = bounds.width < 640;
    const tabletLayout = bounds.width >= 640 && bounds.width < 1024;
    const rewardsBar = banner.querySelector<HTMLElement>(".jnr-home-rewards-bar");
    const speechBubble = banner.querySelector<HTMLElement>(".jnr-home-nixi-speech");
    const rewardsHeight = rewardsBar?.offsetHeight ?? 56;
    const speechHeight = speechBubble ? speechBubble.offsetHeight + 18 : 0;
    const stageHeight = Math.max(0, bounds.height - rewardsHeight - speechHeight);
    const laneWidth = bounds.width;
    const displaySize = phoneLayout
      ? Math.max(150, Math.min(220, stageHeight - 48, bounds.width * 0.62))
      : tabletLayout
        ? Math.max(190, Math.min(360, stageHeight - 48, laneWidth * 0.62))
        : Math.max(210, Math.min(420, stageHeight - 48, bounds.width * 0.58));
    const flightCenterX = 0;
    const flightCenterY = (speechHeight - rewardsHeight) / 2;
    const fullFlightX = Math.max(
      0,
      (laneWidth - displaySize) / 2 - (phoneLayout ? 20 : tabletLayout ? 28 : 36),
    );
    const flightX = fullFlightX * (phoneLayout ? 0.36 : tabletLayout ? 0.5 : 0.58);
    const flightY = Math.max(0, (stageHeight - displaySize) / 2 - 18);

    flightStage.style.width = `${displaySize}px`;
    flightStage.style.height = `${displaySize}px`;
    flightStage.style.setProperty("--jnr-flight-center-x", `${flightCenterX}px`);
    flightStage.style.setProperty("--jnr-flight-center-y", `${flightCenterY}px`);
    flightStage.style.setProperty("--jnr-flight-x", `${flightX}px`);
    flightStage.style.setProperty("--jnr-flight-x-neg", `${-flightX}px`);
    flightStage.style.setProperty("--jnr-flight-y", `${flightY}px`);
    flightStage.style.setProperty("--jnr-flight-y-neg", `${-flightY}px`);
  };

  const flightAreaObserver = new ResizeObserver(fitFlightArea);
  flightAreaObserver.observe(banner);
  const rewardsBar = banner.querySelector<HTMLElement>(".jnr-home-rewards-bar");
  if (rewardsBar) flightAreaObserver.observe(rewardsBar);
  const speechBubble = banner.querySelector<HTMLElement>(".jnr-home-nixi-speech");
  if (speechBubble) flightAreaObserver.observe(speechBubble);
  fitFlightArea();

  const video = document.createElement("video") as FrameVideo;
  video.src = VIDEO_SRC;
  video.autoplay = true;
  video.loop = true;
  video.muted = true;
  video.playsInline = true;
  video.preload = "auto";
  video.setAttribute("aria-hidden", "true");
  video.style.position = "absolute";
  video.style.width = "1px";
  video.style.height = "1px";
  video.style.opacity = "0";
  video.style.pointerEvents = "none";

  companion.insertAdjacentElement("beforebegin", flightStage);
  flightStage.append(canvas);
  flightStage.insertAdjacentElement("afterend", video);

  const context = canvas.getContext("2d", { willReadFrequently: true });
  let lastRenderedTime = -1;

  const renderFrame = () => {
    if (!context || video.readyState < 2 || !video.videoWidth || !video.videoHeight) return;
    if (video.currentTime === lastRenderedTime) return;
    lastRenderedTime = video.currentTime;

    const safeFrameSize = Math.ceil(
      Math.max(video.videoWidth, video.videoHeight) * FRAME_SAFETY_SCALE,
    );
    if (canvas.width !== safeFrameSize || canvas.height !== safeFrameSize) {
      canvas.width = safeFrameSize;
      canvas.height = safeFrameSize;
    }

    context.clearRect(0, 0, canvas.width, canvas.height);
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = "high";
    const drawWidth = video.videoWidth;
    const drawHeight = video.videoHeight;
    const drawX = Math.round((canvas.width - drawWidth) / 2);
    const drawY = Math.round((canvas.height - drawHeight) / 2);
    context.drawImage(video, drawX, drawY, drawWidth, drawHeight);
    const frame = context.getImageData(0, 0, canvas.width, canvas.height);
    removeConnectedBlackBackground(frame, {
      left: drawX,
      right: drawX + drawWidth - 1,
      top: drawY,
      bottom: drawY + drawHeight - 1,
    });
    clearPixVerseWatermark(frame, {
      left: drawX,
      right: drawX + drawWidth - 1,
      top: drawY,
      bottom: drawY + drawHeight - 1,
    });
    context.putImageData(frame, 0, 0);
    canvas.hidden = false;
  };

  const scheduleFrame = () => {
    if (!canvas.isConnected) {
      flightAreaObserver.disconnect();
      return;
    }
    if (video.requestVideoFrameCallback) {
      video.requestVideoFrameCallback(() => {
        renderFrame();
        scheduleFrame();
      });
      return;
    }
    requestAnimationFrame(() => {
      renderFrame();
      scheduleFrame();
    });
  };

  video.addEventListener("loadeddata", renderFrame, { once: true });
  scheduleFrame();
  void video.play().catch(() => undefined);
}

const observer = new MutationObserver(mountJnrHomeVideo);
observer.observe(document.body, { childList: true, subtree: true });
mountJnrHomeVideo();

